import { useState, useEffect } from "react";
import { User, Trash2, Grid3X3, Mail, Bell, ListChecks, CreditCard } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import CatalogPage from "@/pages/CatalogPage";
import EmailContactsTab from "@/components/settings/EmailContactsTab";
import DigestRulesTab from "@/components/settings/DigestRulesTab";
import NotificationsTab from "@/components/settings/NotificationsTab";
import BillingTab from "@/components/settings/BillingTab";
import ForwardingAddressCard from "@/components/settings/ForwardingAddressCard";

const TABS = [
  { id: "account", label: "Account", icon: User },
  { id: "scrapers", label: "Connections", icon: Grid3X3 },
  { id: "email", label: "Email", icon: Mail },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "digest-rules", label: "Digest Rules", icon: ListChecks },
  { id: "billing", label: "Billing", icon: CreditCard },
];

function AccountTab() {
  const { user, logout } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [reason, setReason] = useState("");

  const handleDeleteAccount = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }

    setDeleting(true);
    try {
      const res = await api.delete("/client/account", { data: { reason } });
      const results = res.data?.results || {};
      const failedSteps = Object.entries(results).filter(([_, v]) => v === "failed");

      if (failedSteps.length > 0) {
        setDeleteFailed(true);
        setDeleted(false);
      } else {
        setDeleted(true);
        setDeleteFailed(false);
        setTimeout(() => logout(), 5000);
      }
    } catch (err) {
      setDeleteFailed(true);
      setDeleted(false);
    } finally {
      setDeleting(false);
    }
  };

  if (deleted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Account deleted</h1>
          <p className="text-gray-500 mb-6">
            Your account and all associated data have been permanently removed.
          </p>
          <p className="text-xs text-gray-400">You&apos;ll be redirected to the login page shortly.</p>
        </div>
      </div>
    );
  }

  if (deleteFailed) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h1 className="text-xl font-bold text-red-700 mb-2">Account deletion incomplete</h1>
            <p className="text-sm text-red-600 mb-4">
              We encountered an issue. Some of your data may not have been fully removed.
            </p>
            <a
              href="mailto:eilon@elementarai.com?subject=Account%20deletion%20issue"
              className="inline-block px-4 py-2 rounded-lg text-white text-sm font-medium"
              style={{ backgroundColor: BRAND.bg }}
            >
              Email eilon@elementarai.com
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" style={{ color: BRAND.text }} />
            Account
          </CardTitle>
          <CardDescription>Your account information.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <p className="text-sm font-medium text-gray-700">Name</p>
              <p className="text-sm text-gray-500">{user?.name || "—"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Email</p>
              <p className="text-sm text-gray-500">{user?.email || "—"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Tenant ID</p>
              <p className="text-sm text-gray-500 font-mono text-xs">{user?.tenant_id || "—"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Danger Zone
          </CardTitle>
          <CardDescription>Permanently delete your account and all associated data.</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 mb-4">
            This will permanently delete your account, all portal credentials,
            scraped documents, and extracted data. This action cannot be undone.
          </p>

          {confirmDelete && (
            <div className="space-y-4 mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700 font-medium">
                  Are you sure? This will delete everything — documents, credentials, and all your data.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Help us improve — why are you leaving? (optional)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. switching providers, no longer needed, missing features..."
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-red-400 focus:border-red-400"
                />
              </div>
            </div>
          )}

          <Button
            variant={confirmDelete ? "destructive" : "outline"}
            onClick={handleDeleteAccount}
            disabled={deleting}
            className={confirmDelete ? "" : "border-red-300 text-red-600 hover:bg-red-50"}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {deleting
              ? "Deleting..."
              : confirmDelete
                ? "Yes, permanently delete my account"
                : "Delete Account"}
          </Button>

          {confirmDelete && !deleting && (
            <Button
              variant="ghost"
              className="ml-2"
              onClick={() => { setConfirmDelete(false); setReason(""); }}
            >
              Cancel
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("account");

  // Land on a specific tab via ?tab=<id>, e.g. the Stripe embedded-checkout
  // return_url (?tab=billing&session_id=...) — session_id is left in the URL
  // for BillingTab to read itself.
  useEffect(() => {
    const tab = new URLSearchParams(window.location.search).get("tab");
    if (tab && TABS.some((t) => t.id === tab)) {
      setActiveTab(tab);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle OAuth callback query params: ?email-oauth=success|error&reason=...
  // Uses window.location.search directly to avoid router dependency.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailOauth = params.get("email-oauth");
    if (!emailOauth) return;

    const reason = params.get("reason");

    if (emailOauth === "success") {
      toast.success("Outlook connected");
      // Switch to email tab so user sees the new connected state
      setActiveTab("email");
    } else if (emailOauth === "error") {
      if (reason === "invalid_state") {
        toast.error("The connection link was invalid or expired. Please try connecting again.");
      } else if (reason === "invalid_grant") {
        toast.error("Microsoft could not authorize the connection. Please try again.");
      } else if (reason === "exchange_failed") {
        toast.error("Something went wrong connecting Outlook. Please try again.");
      } else {
        toast.error("Outlook connection failed. Please try again.");
      }
      setActiveTab("email");
    }

    // Scrub query params so refresh does not re-toast
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, "", cleanUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your account, connections, and billing</p>
      </div>

      <ForwardingAddressCard
        address={user?.intake_email}
        firstEmailAt={user?.intake_first_email_at}
        gmailLink={user?.gmail_forward_link}
      />

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            data-tour={
              id === "scrapers" ? "connections"
              : id === "billing" ? "billing"
              : id === "email" ? "email"
              : undefined
            }
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === id
                ? "border-current"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === id ? { color: BRAND.text, borderColor: BRAND.text } : {}}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "account" && <AccountTab />}
      {activeTab === "scrapers" && <CatalogPage />}
      {activeTab === "email" && <EmailContactsTab />}
      {activeTab === "notifications" && <NotificationsTab />}
      {activeTab === "digest-rules" && <DigestRulesTab />}
      {activeTab === "billing" && <BillingTab />}
    </div>
  );
}
